// third-party: emailjs => backend service for sending emails
'use client';

import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import emailjs from 'emailjs-com';
import {
  Mail,
  Send,
  Github,
  Linkedin,
  Instagram,
  Facebook,
  MessageCircle,
  MapPin,
  Clock,
  CheckCircle,
  Loader2,
  Copy,
  ExternalLink,
} from 'lucide-react';
import SectionHeader from './SectionHeader';

const unwrap = (v: string | undefined) => (v ? v.replace(/^['"]|['"]$/g, '').trim() : '');

const getServiceId = () => unwrap(process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID || process.env.EMAILJS_SERVICE_ID);
const getTemplateId = () => unwrap(process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID || process.env.EMAILJS_TEMPLATE_ID);
const getPublicKey = () => unwrap(process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY || process.env.EMAILJS_PUBLIC_KEY);

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const SOCIAL_LINKS = [
  { icon: Github, label: 'GitHub', handle: '@Marwan-Hussein', href: 'https://github.com/Marwan-Hussein' },
  { icon: Linkedin, label: 'LinkedIn', handle: 'marwanhussein9', href: 'https://www.linkedin.com/in/marwanhussein9' },
  { icon: MessageCircle, label: 'WhatsApp', handle: 'Send a message', href: 'https://wa.me/qr/BQP37VAJ5RB4J1' },
  { icon: Instagram, label: 'Instagram', handle: '@marwan_hussein9', href: 'https://www.instagram.com/marwan_hussein9?igsh=MWNzejU5em9hZDIyYw==' },
  { icon: Facebook, label: 'Facebook', handle: 'Marwan Hussein', href: 'https://www.facebook.com/share/1JALCAMUu4/' },
];

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.15 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('marwan.hussein.450@gmail.com');
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } catch {
      toast.error('Unable to copy email address');
    }
  };

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    const serviceId = getServiceId();
    const templateId = getTemplateId();
    const publicKey = getPublicKey();

    if (!serviceId || !templateId || !publicKey) {
      toast.error('Contact form is not configured', {
        description: 'Please email marwan.hussein.450@gmail.com directly.',
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const templateParams = {
        name: data.name,
        email: data.email,
        from_name: data.name,
        from_email: data.email,
        user_name: data.name,
        user_email: data.email,
        reply_to: data.email,
        subject: data.subject,
        message: data.message,
        details: `Full Name: ${data.name}\nEmail: ${data.email}\nSubject: ${data.subject}\n\nMessage:\n${data.message}`,
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      toast.success('Message sent successfully!', {
        description: "Thanks for reaching out. I'll reply soon.",
      });
      reset();
      setSubmitted(true);
    } catch (err) {
      console.error('EmailJS send error:', err);
      toast.error('Failed to send message', {
        description: 'Please try emailing directly at marwan.hussein.450@gmail.com',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldError = (hasError: boolean) =>
    hasError ? 'input-field input-field-error' : 'input-field';

  return (
    <section id="contact" ref={sectionRef} className="py-28 xl:py-40 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div
          className={`transition-all duration-800 ease-organic ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <SectionHeader
            index="07 — Contact"
            title="Let's"
            highlight="connect"
            description="Have a project in mind, a role to fill, or just want to say hello? I'll respond within 24 hours."
            align="center"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-16">
          <div
            className={`lg:col-span-4 transition-all duration-800 delay-100 ease-organic ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <div className="warm-card rounded-2xl p-6 mb-6">
              <p className="font-mono text-[10px] text-parchment-muted uppercase tracking-widest mb-5">
                Direct contact
              </p>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 rounded-xl bg-sand/5 border border-sand/15">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-sand/10 flex items-center justify-center shrink-0">
                      <Mail size={15} className="text-sand" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-parchment-muted text-[10px] font-mono uppercase mb-0.5">Email</div>
                      <div className="text-parchment text-sm truncate">marwan.hussein.450@gmail.com</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="p-2 rounded-lg text-parchment-muted hover:text-sand transition-colors duration-200 shrink-0"
                    aria-label="Copy email address"
                  >
                    {copiedEmail ? <CheckCircle size={14} className="text-sage" /> : <Copy size={14} />}
                  </button>
                </div>

                <div className="flex items-center gap-3 p-4 rounded-xl border border-parchment/8">
                  <div className="w-9 h-9 rounded-lg bg-parchment/5 flex items-center justify-center">
                    <MapPin size={15} className="text-parchment-muted" />
                  </div>
                  <div>
                    <div className="text-parchment-muted text-[10px] font-mono uppercase mb-0.5">Location</div>
                    <div className="text-parchment text-sm">Giza, Egypt</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 rounded-xl bg-sage/5 border border-sage/15">
                  <div className="w-9 h-9 rounded-lg bg-sage/10 flex items-center justify-center">
                    <Clock size={15} className="text-sage" />
                  </div>
                  <div>
                    <div className="text-parchment-muted text-[10px] font-mono uppercase mb-0.5">Response</div>
                    <div className="text-sage text-sm">Usually within 24 hours</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="warm-card rounded-2xl p-6">
              <p className="font-mono text-[10px] text-parchment-muted uppercase tracking-widest mb-4">
                Social profiles
              </p>
              <div className="space-y-2">
                {SOCIAL_LINKS.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-xl border border-parchment/6 hover:border-sand/20 hover:bg-sand/5 transition-all duration-300 group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg border border-parchment/8 flex items-center justify-center group-hover:border-sand/25 transition-colors">
                          <Icon size={15} className="text-parchment-muted group-hover:text-sand transition-colors" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-parchment text-sm group-hover:text-sand transition-colors">
                            {social.label}
                          </div>
                          <div className="text-parchment-muted text-xs font-mono truncate">{social.handle}</div>
                        </div>
                      </div>
                      <ExternalLink size={12} className="text-parchment-muted/30 group-hover:text-parchment-muted shrink-0" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            className={`lg:col-span-8 lg:mt-8 transition-all duration-800 delay-200 ease-organic ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
            }`}
          >
            <div className="warm-card rounded-2xl p-6 xl:p-10">
              <p className="font-mono text-[10px] text-sand uppercase tracking-widest mb-8">
                Send a message
              </p>

              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-14 h-14 rounded-full bg-sage/10 border border-sage/25 flex items-center justify-center mb-4">
                    <CheckCircle size={26} className="text-sage" />
                  </div>
                  <h3 className="font-display text-parchment text-lg font-700 mb-2">Message sent</h3>
                  <p className="text-parchment-muted text-sm max-w-xs">
                    Thanks for reaching out — I&apos;ll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-parchment-muted text-xs font-mono uppercase tracking-wider mb-2">
                        Full name <span className="text-sand">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        placeholder="Your name"
                        className={fieldError(!!errors.name)}
                        {...register('name', {
                          required: 'Name is required',
                          minLength: { value: 2, message: 'Name must be at least 2 characters' },
                        })}
                      />
                      {errors.name && (
                        <p className="mt-1.5 text-rose text-xs">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-parchment-muted text-xs font-mono uppercase tracking-wider mb-2">
                        Email <span className="text-sand">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        placeholder="you@email.com"
                        className={fieldError(!!errors.email)}
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: 'Please enter a valid email address',
                          },
                        })}
                      />
                      {errors.email && (
                        <p className="mt-1.5 text-rose text-xs">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-parchment-muted text-xs font-mono uppercase tracking-wider mb-2">
                      Subject <span className="text-sand">*</span>
                    </label>
                    <input
                      id="subject"
                      type="text"
                      placeholder="What's this about?"
                      className={fieldError(!!errors.subject)}
                      {...register('subject', {
                        required: 'Subject is required',
                        minLength: { value: 5, message: 'Subject must be at least 5 characters' },
                      })}
                    />
                    {errors.subject && (
                      <p className="mt-1.5 text-rose text-xs">{errors.subject.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-parchment-muted text-xs font-mono uppercase tracking-wider mb-2">
                      Message <span className="text-sand">*</span>
                    </label>
                    <textarea
                      id="message"
                      rows={6}
                      placeholder="Tell me about your project, role, or just say hello…"
                      className={`${fieldError(!!errors.message)} resize-none`}
                      {...register('message', {
                        required: 'Message is required',
                        minLength: { value: 20, message: 'Message must be at least 20 characters' },
                      })}
                    />
                    {errors.message && (
                      <p className="mt-1.5 text-rose text-xs">{errors.message.message}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-4 pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary min-w-[160px] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={15} className="animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          <Send size={15} />
                          Send message
                        </>
                      )}
                    </button>
                    <p className="text-parchment-muted/50 text-xs font-mono">No spam — real conversations only.</p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
